/**
 * The four things most recently worked on, as thumbnails, for an empty Analyser, Tagging or Composer
 * page: one click resumes it. Newest on the left; Load more opens the full list (the Library for clips,
 * the Archive for compositions).
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { RECENT_FILES_COUNT } from '@studio/contracts';
import { trpc } from '@/lib/trpc';

export interface RecentItem { id: string; name: string; posterUrl: string | null }

/** Load more: to the Archive for compositions, to the Library for clips (remembering Tagging, which sent the user). */
function MoreLink({ to }: { to: '/archive' | 'library' | 'library-from-tagging' }) {
  if (to === '/archive') return <Link to="/archive" className="btn quiet recents-more">Load more</Link>;
  return <Link to="/library" search={to === 'library-from-tagging' ? { from: 'tagging' } : {}} className="btn quiet recents-more">Load more</Link>;
}

export function Recents({ items, onOpen, moreTo }: { items: RecentItem[]; onOpen: (id: string) => void; moreTo: '/archive' | 'library' | 'library-from-tagging' }) {
  if (!items.length) return null;
  return (
    <section className="recents" aria-label="Recent files">
      <div className="label">Recents</div>
      <div className="recents-grid">
        {items.slice(0, RECENT_FILES_COUNT).map((a) => (
          <button key={a.id} type="button" className="recent-card" onClick={() => onOpen(a.id)}>
            {a.posterUrl ? <img src={a.posterUrl} alt="" /> : <span className="recent-empty" />}
            <span className="recent-name">{a.name}</span>
          </button>
        ))}
        <MoreLink to={moreTo} />
      </div>
    </section>
  );
}

/** Clips, for the Analyser and Tagging; a clip picked in the Library returns to whichever sent the user. */
export function RecentFiles({ onOpen, from }: { onOpen: (assetId: string) => void; from?: 'tagging' }) {
  const recent = useQuery(trpc.assets.imports.queryOptions({ filter: 'analysed', limit: RECENT_FILES_COUNT, sort: 'updated' }));
  return <Recents items={recent.data ?? []} onOpen={onOpen} moreTo={from === 'tagging' ? 'library-from-tagging' : 'library'} />;
}
